import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { holdsIdentityAtom, claimsAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import { getSDK } from '../services/sdk-instance';
import type {
  ProductOnboardingPhase,
  ProductOnboardingStatus,
  DeferProductOnboardingResponse,
} from '@localagent/sdk';
import { isNotFound } from '@localagent/sdk';
import { useOnboardingStatus } from './useBilling';

export function isProductOnboardingActive(
  phase: ProductOnboardingPhase | undefined | null,
): boolean {
  return phase != null && phase !== 'complete' && phase !== 'deferred';
}

export function useProductOnboardingStatus() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;
  const { data: commercialStatus, isFetched: commercialFetched } = useOnboardingStatus();

  // Product onboarding only exists after commercial complete. Legacy accounts
  // (commercial status null) skip the query entirely.
  const commercialComplete = commercialStatus?.phase === 'complete';
  const enabled = Boolean(
    isAuthenticated && sdkConfigured && accountId && commercialFetched && commercialComplete,
  );

  const query = useQuery<ProductOnboardingStatus | null, Error>({
    queryKey: ['account', 'product-onboarding', accountId],
    queryFn: async () => {
      try {
        return await getSDK().account.getProductOnboardingStatus(accountId as string);
      } catch (err) {
        if (isNotFound(err)) {
          return null;
        }
        const status = (err as { status?: number; statusCode?: number }).status
          ?? (err as { status?: number; statusCode?: number }).statusCode;
        const message = err instanceof Error ? err.message : String(err);
        if (status === 404 || /not found/i.test(message)) {
          return null;
        }
        throw err;
      }
    },
    enabled,
    staleTime: 5 * 1000,
    refetchInterval: (q) => {
      const data = q.state.data;
      // After commercial complete, keep polling until the product workflow is
      // visible (brief Temporal start race) or reaches a terminal phase.
      if (data === null || data === undefined) {
        return 2 * 1000;
      }
      if (!isProductOnboardingActive(data.phase)) {
        return false;
      }
      if (!data.session_id) {
        return 2 * 1000;
      }
      return 5 * 1000;
    },
  });

  return { ...query, isEnabled: enabled };
}

export function useDeferProductOnboarding() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const claims = useAtomValue(claimsAtom);
  const accountId = claims?.accountId;

  return useMutation<DeferProductOnboardingResponse, Error, void>({
    mutationFn: () => getSDK().account.deferProductOnboarding(accountId as string),
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({
          queryKey: ['account', 'product-onboarding'],
        });
      }
    },
  });
}
